import { lazy, Suspense, useState } from "react";
import { Link } from "wouter";
import {
  AlertTriangle,
  CircleDollarSign,
  ClipboardList,
  PackageCheck,
  Percent,
  Store,
  Users,
} from "lucide-react";
import { PageHeader } from "@/components/layout/PageHeader";
import { MetricCard } from "@/components/dashboard/MetricCard";
import { OrdersChart } from "@/components/dashboard/OrdersChart";
import { RegionList } from "@/components/dashboard/RegionList";
import { ORDER_STATUS_LABEL } from "@/components/dashboard/OrderStatusBadge";
import { BILLING_STATUS_LABEL } from "@/components/sellers/BillingStatusBadge";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import { Badge } from "@/components/ui/badge";
import { DASHBOARD_RANGES, useDashboard, type DashboardRange } from "@/hooks/use-dashboard";
import { useBillingConfig } from "@/hooks/use-config";
import { formatCurrency, formatDate, formatNumber } from "@/lib/format";
import type { BillingStatusValue } from "@/types";

const DEFAULT_FEE = 10;

// O mapa traz o Leaflet e o Supercluster, que só esta tela usa: carregá-los sob
// demanda mantém o pacote principal do painel do mesmo tamanho.
const PresenceMapCard = lazy(() => import("@/components/dashboard/PresenceMapCard"));

export default function Dashboard() {
  const [range, setRange] = useState<DashboardRange>("30d");
  const billingConfig = useBillingConfig();
  // A mesma reserva que o backend usa quando o documento não existe.
  const fee = billingConfig.data?.platformFeePercentage ?? DEFAULT_FEE;
  const { data, isLoading, isError, error } = useDashboard(range, fee);

  return (
    <>
      <PageHeader
        title="Painel"
        description="Como o negócio está indo, no período escolhido."
        actions={
          <Select value={range} onValueChange={(v) => setRange(v as DashboardRange)}>
            <SelectTrigger className="w-36" aria-label="Período">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {Object.entries(DASHBOARD_RANGES).map(([key, cfg]) => (
                <SelectItem key={key} value={key}>
                  {cfg.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        }
      />

      {isError ? (
        <Card className="border-destructive">
          <CardHeader>
            <CardTitle className="text-destructive">Não foi possível carregar o painel</CardTitle>
            <CardDescription>
              {(error as Error)?.message ??
                "Verifique a conexão e as permissões da sua conta."}
            </CardDescription>
          </CardHeader>
        </Card>
      ) : null}

      <section className="grid grid-cols-1 gap-3 xs:grid-cols-2 lg:grid-cols-4">
        <MetricCard
          label="Receita entregue"
          value={formatCurrency(data?.deliveredRevenue)}
          hint={`${formatNumber(data?.deliveredOrders)} pedidos entregues`}
          icon={CircleDollarSign}
          tone="ok"
          loading={isLoading}
        />
        <MetricCard
          label="Taxa da plataforma"
          value={formatCurrency(data?.estimatedPlatformFee)}
          hint={`${fee}% sobre o entregue no período`}
          icon={Percent}
          loading={isLoading}
        />
        <MetricCard
          label="Ticket médio"
          value={formatCurrency(data?.averageTicket)}
          hint={`${formatNumber(data?.ordersInPeriod)} pedidos criados`}
          icon={PackageCheck}
          loading={isLoading}
        />
        <MetricCard
          label="Pedidos em aberto"
          value={formatNumber(data?.openOrders)}
          hint={`${formatNumber(data?.cancelledOrders)} cancelados no período`}
          icon={ClipboardList}
          tone={data && data.openOrders > 0 ? "warning" : "default"}
          loading={isLoading}
        />
      </section>

      <section className="mt-3 grid grid-cols-1 gap-3 xs:grid-cols-2 lg:grid-cols-4">
        <MetricCard
          label="Clientes"
          value={formatNumber(data?.totalClients)}
          hint="Contas cadastradas"
          icon={Users}
          loading={isLoading}
        />
        <MetricCard
          label="Vendedores"
          value={formatNumber(data?.totalSellers)}
          hint={`${formatNumber(data?.activeSellers)} ativos, ${formatNumber(data?.openSellers)} abertos agora`}
          icon={Store}
          loading={isLoading}
        />
        <MetricCard
          label="Bloqueados"
          value={formatNumber(data?.blockedSellers)}
          hint="Fora do ar por fatura vencida"
          icon={AlertTriangle}
          tone={data && data.blockedSellers > 0 ? "error" : "default"}
          loading={isLoading}
        />
        <MetricCard
          label="Em atraso"
          value={formatNumber(data?.sellersByBillingStatus?.Expired ?? 0)}
          hint="Vendedores com fatura vencida"
          icon={AlertTriangle}
          tone={(data?.sellersByBillingStatus?.Expired ?? 0) > 0 ? "error" : "default"}
          loading={isLoading}
        />
      </section>

      <section className="mt-6">
        <Card>
          <CardHeader>
            <CardTitle>Movimento do período</CardTitle>
            <CardDescription>
              Receita dos pedidos entregues e quantidade de pedidos criados, por dia. A receita conta
              pela data de entrega, que é a mesma régua do faturamento dos vendedores.
              {data ? ` Desde ${formatDate(data.since)}.` : ""}
            </CardDescription>
          </CardHeader>
          <CardContent>
            {isLoading ? <Skeleton className="h-64 w-full" /> : <OrdersChart data={data?.daily ?? []} />}
          </CardContent>
        </Card>
      </section>

      <section className="mt-6 grid grid-cols-1 gap-3 lg:grid-cols-2">
        <Card>
          <CardHeader>
            <CardTitle>Pedidos por situação</CardTitle>
            <CardDescription>Todos os pedidos criados no período.</CardDescription>
          </CardHeader>
          <CardContent>
            {isLoading ? (
              <Skeleton className="h-40 w-full" />
            ) : data && data.statusBreakdown.length > 0 ? (
              <ul className="space-y-2">
                {data.statusBreakdown.map((row) => (
                  <li key={row.status} className="flex items-center justify-between gap-2">
                    <span className="min-w-0 truncate text-small">
                      {ORDER_STATUS_LABEL[row.status] ?? row.status}
                    </span>
                    <span className="shrink-0 text-small font-bold tabular-nums">
                      {formatNumber(row.count)}
                    </span>
                  </li>
                ))}
              </ul>
            ) : (
              <p className="py-4 text-small text-muted-foreground">Nenhum pedido no período.</p>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Cobrança dos vendedores</CardTitle>
            <CardDescription>Situação atual, independente do período.</CardDescription>
          </CardHeader>
          <CardContent>
            {isLoading ? (
              <Skeleton className="h-40 w-full" />
            ) : data && Object.keys(data.sellersByBillingStatus).length > 0 ? (
              <ul className="space-y-2">
                {Object.entries(data.sellersByBillingStatus)
                  .sort((a, b) => b[1] - a[1])
                  .map(([status, count]) => (
                    <li key={status} className="flex items-center justify-between gap-2">
                      <span className="min-w-0 truncate text-small">
                        {BILLING_STATUS_LABEL[status as BillingStatusValue] ?? status}
                      </span>
                      <span className="shrink-0 text-small font-bold tabular-nums">
                        {formatNumber(count)}
                      </span>
                    </li>
                  ))}
              </ul>
            ) : (
              <p className="py-4 text-small text-muted-foreground">
                Nenhum vendedor com cobrança configurada.
              </p>
            )}
          </CardContent>
        </Card>
      </section>

      <section className="mt-3 grid grid-cols-1 gap-3 lg:grid-cols-2">
        <RegionList
          title="Por estado"
          description="Clientes e estabelecimentos, pela localização do endereço. A grafia digitada não separa mais o mesmo estado em dois."
          rows={data?.byState ?? []}
          emptyText="Nenhum endereço com localização ou estado."
          itemLabel="estados"
        />
        <RegionList
          title="Por cidade"
          description={
            data?.withoutRegion
              ? `Pela localização do endereço. ${formatNumber(data.withoutRegion)} cadastros sem endereço ficaram de fora.`
              : "Pela localização do endereço, da cidade com mais presença para a com menos."
          }
          rows={data?.byCity ?? []}
          emptyText="Nenhum endereço com localização ou cidade."
          itemLabel="cidades"
        />
      </section>

      <section className="mt-3">
        <Card>
          <CardHeader>
            <CardTitle>Vendedores que mais faturaram</CardTitle>
            <CardDescription>Pela receita entregue no período.</CardDescription>
          </CardHeader>
          <CardContent>
            {isLoading ? (
              <Skeleton className="h-40 w-full" />
            ) : data && data.topSellers.length > 0 ? (
              <ol className="space-y-2">
                {data.topSellers.map((row, index) => (
                  <li key={row.sellerId}>
                    <Link
                      href={`/vendedores/${row.sellerId}`}
                      className="flex items-center gap-3 rounded-md px-2 py-2 transition-colors hover:bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                    >
                      <span className="w-5 shrink-0 text-caption font-bold tabular-nums text-muted-foreground">
                        {index + 1}
                      </span>
                      <span className="min-w-0 flex-1 truncate text-small font-semibold">{row.name}</span>
                      <Badge variant="muted" className="shrink-0">
                        {formatNumber(row.orders)} pedidos
                      </Badge>
                      <span className="shrink-0 text-small font-bold tabular-nums">
                        {formatCurrency(row.revenue)}
                      </span>
                    </Link>
                  </li>
                ))}
              </ol>
            ) : (
              <p className="py-4 text-small text-muted-foreground">
                Nenhum pedido entregue no período.
              </p>
            )}
          </CardContent>
        </Card>
      </section>

      <section className="mt-3">
        <Suspense fallback={<Skeleton className="h-[30rem] w-full rounded-lg" />}>
          <PresenceMapCard
            points={data?.mapPoints}
            totals={data ? { clients: data.totalClients, sellers: data.totalSellers } : undefined}
            loading={isLoading}
          />
        </Suspense>
      </section>
    </>
  );
}
